import { useEffect, useRef, useState, type ReactNode } from "react";
import type { Editor } from "@tiptap/core";
import type { CastMember, EpisodeView, Id, Scene } from "@supermanus/document-model";
import { FormatToolbar } from "./FormatToolbar";
import { CastEditor } from "./CastEditor";
import { orderedEpisodeTabs, workspaceView } from "./EpisodeHome";
import { SHOW_REPLIK_UI } from "./replikFeature";

function sceneOptionLabel(scene: Scene): string {
  const title = scene.title.trim() || scene.heading?.trim() || "Unavngiven";
  return `${scene.number}. ${title}`;
}

export interface EpisodePageBarProps {
  view: EpisodeView;
  scenes: Scene[];
  sceneId: Id | null;
  readOnly?: boolean;
  onSelectScene: (sceneId: Id) => void;
  onChangeView: (view: EpisodeView) => void;
  tabOrder?: EpisodeView[];
  onAddScene: () => void;
  onAddDialogue?: (sceneId: Id) => void;
  activeEditor: Editor | null;
  rowSpanMode: boolean;
  onToggleRowSpanMode: () => void;
  onOpenHelp: () => void;
  onOpenSettings: () => void;
  onPreparePrint: () => void;
  castMembers: CastMember[];
  onAddCastMember: (name: string) => void;
  onUpdateCastMember: (
    memberId: Id,
    patch: Partial<
      Pick<CastMember, "name" | "color" | "showNameAboveLines">
    >,
  ) => void;
  onRemoveCastMember: (memberId: Id) => void;
  /** Episode name on the same row as Help / Settings. */
  title?: ReactNode;
  onGoDashboard?: () => void;
  className?: string;
  onOpenFind?: () => void;
  findOpen?: boolean;
  episodeZoom?: number;
  onEpisodeZoomChange?: (zoom: number) => void;
}

function SceneNav({
  scenes,
  sceneId,
  onSelectScene,
  disabled,
}: {
  scenes: Scene[];
  sceneId: Id | null;
  onSelectScene: (sceneId: Id) => void;
  disabled?: boolean;
}) {
  const sceneIndex = scenes.findIndex((s) => s.id === sceneId);
  const scene = sceneIndex >= 0 ? scenes[sceneIndex] : null;
  return (
    <div
      className={`sm-page-scene-nav${disabled ? " is-disabled" : ""}`}
      aria-disabled={disabled || undefined}
    >
      <button
        type="button"
        disabled={disabled || sceneIndex <= 0}
        aria-label="Forrige scene"
        onClick={() => {
          const prev = scenes[sceneIndex - 1];
          if (prev) onSelectScene(prev.id);
        }}
      >
        ‹
      </button>
      <select
        aria-label="Vælg scene"
        value={scene?.id ?? ""}
        disabled={disabled || scenes.length === 0}
        onChange={(e) => {
          if (e.target.value) onSelectScene(e.target.value);
        }}
      >
        {scenes.length === 0 ? (
          <option value="">Ingen scener</option>
        ) : (
          scenes.map((s) => (
            <option key={s.id} value={s.id}>
              {sceneOptionLabel(s)}
            </option>
          ))
        )}
      </select>
      <button
        type="button"
        disabled={
          disabled || sceneIndex < 0 || sceneIndex >= scenes.length - 1
        }
        aria-label="Næste scene"
        onClick={() => {
          const next = scenes[sceneIndex + 1];
          if (next) onSelectScene(next.id);
        }}
      >
        ›
      </button>
    </div>
  );
}

function TabNav({
  view,
  tabOrder,
  onChangeView,
}: {
  view: EpisodeView;
  tabOrder?: EpisodeView[];
  onChangeView: (view: EpisodeView) => void;
}) {
  const tabs = orderedEpisodeTabs(tabOrder);
  const active = workspaceView(view);
  return (
    <div className="sm-page-tab-nav">
      <select
        aria-label="Fanevælger"
        title="Fanevælger"
        value={active}
        onChange={(e) => {
          onChangeView(e.target.value as EpisodeView);
        }}
      >
        {tabs.map((tab) => (
          <option key={tab.id} value={tab.id}>
            {tab.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function ZoomMenu({
  episodeZoom,
  onEpisodeZoomChange,
}: {
  episodeZoom: number;
  onEpisodeZoomChange: (zoom: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDoc);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="sm-zoom-menu" ref={rootRef}>
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        className={open ? "is-active" : undefined}
        title={`Zoom ${Math.round(episodeZoom * 100)}%`}
        onClick={() => setOpen((v) => !v)}
      >
        Zoom
      </button>
      {open ? (
        <div className="sm-zoom-menu-pop is-end" role="menu">
          <div className="sm-zoom-controls" role="group" aria-label="Zoom">
            <button
              type="button"
              title="Zoom ud (Ctrl/⌘+−)"
              disabled={episodeZoom <= 0.75}
              onClick={() =>
                onEpisodeZoomChange(
                  Math.round((episodeZoom - 0.1) * 10) / 10,
                )
              }
            >
              −
            </button>
            <button
              type="button"
              title="Nulstil zoom (Ctrl/⌘+0)"
              onClick={() => onEpisodeZoomChange(1)}
            >
              {Math.round(episodeZoom * 100)}%
            </button>
            <button
              type="button"
              title="Zoom ind (Ctrl/⌘++)"
              disabled={episodeZoom >= 1.5}
              onClick={() =>
                onEpisodeZoomChange(
                  Math.round((episodeZoom + 0.1) * 10) / 10,
                )
              }
            >
              +
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function EpisodePageBar({
  view,
  scenes,
  sceneId,
  readOnly,
  onSelectScene,
  onChangeView,
  tabOrder,
  onAddScene,
  onAddDialogue,
  activeEditor,
  rowSpanMode,
  onToggleRowSpanMode,
  onOpenHelp,
  onOpenSettings,
  onPreparePrint,
  castMembers,
  onAddCastMember,
  onUpdateCastMember,
  onRemoveCastMember,
  title,
  onGoDashboard,
  className,
  onOpenFind,
  findOpen,
  episodeZoom = 1,
  onEpisodeZoomChange,
}: EpisodePageBarProps) {
  const scene = scenes.find((s) => s.id === sceneId) ?? null;
  const scriptView = view === "script" || view === "sources";
  const scriptToolsActive = scriptView && Boolean(scene);
  const showCast = SHOW_REPLIK_UI && scriptView;

  const [castOpen, setCastOpen] = useState(false);

  useEffect(() => {
    setCastOpen(false);
  }, [view]);

  return (
    <div
      className={`sm-page-chrome${className ? ` ${className}` : ""}`}
    >
      {/* Linje 1: tilbage · titel · view-knapper (ikke manus-specifikke) */}
      <div className="sm-page-bar sm-page-bar-title">
        {onGoDashboard ? (
          <button
            type="button"
            className="sm-home-link"
            onClick={onGoDashboard}
            title="Tilbage til produktionsforsiden"
          >
            ← Produktion
          </button>
        ) : null}
        {title ? (
          <div className="sm-folder-episode-row">{title}</div>
        ) : null}
        <div className="sm-page-bar-end">
          {showCast ? (
            <button
              type="button"
              className={castOpen ? "is-active" : undefined}
              aria-expanded={castOpen}
              onClick={() => setCastOpen((v) => !v)}
            >
              Medvirkende
            </button>
          ) : null}
          <button type="button" disabled={readOnly} onClick={onAddScene}>
            Tilføj scene
          </button>
          {onOpenFind ? (
            <button
              type="button"
              className={findOpen ? "is-active" : undefined}
              title="Søg i scene eller episode (Ctrl/⌘+F)"
              aria-pressed={findOpen}
              onClick={onOpenFind}
            >
              Søg
            </button>
          ) : null}
          {onEpisodeZoomChange ? (
            <ZoomMenu
              episodeZoom={episodeZoom}
              onEpisodeZoomChange={onEpisodeZoomChange}
            />
          ) : null}
          <button type="button" onClick={onOpenHelp}>
            Hjælp
          </button>
          <button type="button" onClick={onOpenSettings}>
            Indstillinger
          </button>
          <button
            type="button"
            title="Forbered udskrift og eksportér til PDF, Word eller RTF"
            onClick={onPreparePrint}
          >
            Eksportér/Udskriv
          </button>
        </div>
      </div>

      {/* Linje 2: scenevælger altid aktiv; manus-værktøjer kun i manus/noter */}
      <div className="sm-page-bar sm-page-bar-tools">
        <SceneNav
          scenes={scenes}
          sceneId={sceneId}
          onSelectScene={(id) => {
            // Stay in Noter when already there; other tabs open Manus.
            if (view !== "script" && view !== "sources") {
              onChangeView("script");
            }
            onSelectScene(id);
            window.scrollTo(0, 0);
          }}
          disabled={scenes.length === 0}
        />
        <FormatToolbar
          editor={scriptToolsActive ? activeEditor : null}
          disabled={readOnly || !scriptToolsActive}
          showMarks
          className="sm-format-toolbar-page"
          rowSpanMode={rowSpanMode}
          onToggleRowSpanMode={onToggleRowSpanMode}
        />
        <TabNav
          view={view}
          tabOrder={tabOrder}
          onChangeView={onChangeView}
        />
        {SHOW_REPLIK_UI ? (
          <button
            type="button"
            disabled={readOnly || !scriptToolsActive || !scene}
            onClick={() => scene && onAddDialogue?.(scene.id)}
          >
            Tilføj replik
          </button>
        ) : null}
      </div>

      {showCast && castOpen ? (
        <div className="sm-page-cast">
          <CastEditor
            members={castMembers}
            readOnly={readOnly}
            onAdd={onAddCastMember}
            onUpdate={onUpdateCastMember}
            onRemove={onRemoveCastMember}
          />
        </div>
      ) : null}
    </div>
  );
}
